import { useState } from 'react';
import { Nil } from '../../components/ui/Table';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { useAuth } from '../../hooks/useAuth';
import {
  useUpdateProfile,
  useChangePassword,
  useApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useUpdateOptionalFeature,
  useGoogleAccountStatus,
  useDisconnectGoogleAccount,
} from '../../hooks/useSettings';
import { getGoogleAccountAuthUrl } from '../../api/settings';
import { FEATURES } from '@sequesto-os/shared';
import { TwoFactorSettings } from '../../components/two-factor';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { KeyRound, Plus } from 'lucide-react';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { LoadingState } from '../../components/ui/States';
import { useAccess } from '../../hooks/useAccess';
import { ReminderSettingsCard } from '../Organizer/ReminderSettingsCard';

/**
 * Opt-in features nothing gates on any more, so a switch for them changes
 * nothing. `organizer.reminders`: Reminders moved to Settings › Notifications
 * and no route, tab or API check reads the feature.
 */
const RETIRED_OPT_INS = new Set<string>(['organizer.reminders']);

/** Catalog labels that predate a rename in the UI. */
const LABEL_OVERRIDES: Record<string, string> = {
  'organizer.inbox': 'Suggested',
};

type FeatureDef = (typeof FEATURES)[number];

/** Sections hidden for everyone by default; admins can opt in below, grouped by area. */
const OPTIONAL_GROUPS: { area: string; items: { id: FeatureDef['id']; label: string }[] }[] = (() => {
  const optIn = FEATURES.filter((f) => 'adminOptIn' in f && f.adminOptIn && !RETIRED_OPT_INS.has(f.id));
  const groups = new Map<string, { id: FeatureDef['id']; label: string }[]>();
  for (const f of optIn) {
    const parentId = 'parentId' in f ? f.parentId : undefined;
    const area = parentId ? FEATURES.find((p) => p.id === parentId)?.label ?? parentId : f.label;
    // "GTM — Match review" under the GTM heading reads as "Match review"; the area itself is "Whole section".
    const label = LABEL_OVERRIDES[f.id] ?? (parentId ? f.label.replace(/^.*?\s—\s/, '') : 'Whole section');
    groups.set(area, [...(groups.get(area) ?? []), { id: f.id, label }]);
  }
  return [...groups.entries()].map(([area, items]) => ({ area, items }));
})();

const STALE_KEY_MS = 60 * 24 * 60 * 60 * 1000;

/** A key nobody has used in 60 days (or ever, 60 days after creation) is a quiet exception. */
function isStaleKey(key: { createdAt: string; lastUsedAt?: string | null }): boolean {
  const last = key.lastUsedAt ?? key.createdAt;
  return Date.now() - new Date(last).getTime() > STALE_KEY_MS;
}

export function Settings() {
  const { user, refreshUser } = useAuth();
  const { canAccess } = useAccess();
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);

  const { data: apiKeysData, isLoading: apiKeysLoading } = useApiKeys();
  const { data: notifPrefsData } = useNotificationPreferences();
  const { data: googleAccountData } = useGoogleAccountStatus();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const createApiKey = useCreateApiKey();
  const deleteApiKey = useDeleteApiKey();
  const updateNotificationPrefs = useUpdateNotificationPreferences();
  const updateOptionalFeature = useUpdateOptionalFeature();
  const disconnectGoogle = useDisconnectGoogleAccount();

  const apiKeys = apiKeysData?.apiKeys || [];
  const notificationPrefs = notifPrefsData?.preferences;

  return (
    <PageShell>
      <PageHeader eyebrow="Admin" title="Settings" subtitle="Your account and preferences." />

      {/* Profile Section */}
      <Card>
        <CardHeader
          title="Profile"
          description="Your account information"
          action={
            <Button variant="outline" size="sm" onClick={() => setShowEditProfileModal(true)}>
              Edit profile
            </Button>
          }
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <ProfileField label="Name" value={user?.name} />
          <ProfileField label="Email" value={user?.email} />
          <ProfileField
            label="Role"
            value={
              <Badge variant="default" size="md">
                {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : <Nil />}
              </Badge>
            }
          />
        </div>
      </Card>

      {/* Security Section */}
      <Card>
        <CardHeader
          title="Security"
          description="Manage your password and authentication"
        />
        {user?.role === 'admin' && user.twoFactorEnabled === false && (
          <p className="mb-2 flex items-center gap-2 text-body-xs text-warning-dark">
            <span className="h-1.5 w-1.5 flex-none rounded-full bg-warning" aria-hidden />
            Two-factor authentication is off on an admin account. Turn it on below.
          </p>
        )}
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border-light">
            <div>
              <p className="text-body-sm font-medium text-text-primary">Password</p>
              <p className="text-body-xs text-text-tertiary">Change your account password</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowChangePasswordModal(true)}>
              Change password
            </Button>
          </div>
          <TwoFactorSettings />
        </div>
      </Card>

      {/* Connected Accounts Section */}
      <Card>
        <CardHeader
          title="Connected accounts"
          description="Link external accounts for agent tools"
        />
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border-light last:border-0">
            <div className="flex items-center gap-3">
              <svg className="h-6 w-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div>
                <p className="text-body-sm font-medium text-text-primary">Google Workspace</p>
                {googleAccountData?.connected ? (
                  <p className="text-body-xs text-text-tertiary">
                    {googleAccountData.googleEmail}
                    {googleAccountData.status === 'revoked' && (
                      <span className="ml-2 text-error">Access revoked: please reconnect</span>
                    )}
                    {googleAccountData.status === 'active' && googleAccountData.connectedAt && (
                      <span> · connected {formatDayMonthYear(googleAccountData.connectedAt)}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-body-xs text-text-tertiary">
                    Connect your Google account to use Docs, Sheets, Gmail and Calendar from the OS
                  </p>
                )}
              </div>
            </div>
            {googleAccountData?.connected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm('Disconnect your Google account? The OS will no longer be able to access your Google Workspace.')) {
                    disconnectGoogle.mutate();
                  }
                }}
                disabled={disconnectGoogle.isPending}
              >
                {disconnectGoogle.isPending ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  const { url } = await getGoogleAccountAuthUrl();
                  window.location.href = url;
                }}
              >
                Connect Google account
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* API Access Section — viewers use the OS through the website only */}
      {user?.role !== 'viewer' && (
      <Card>
        <CardHeader
          title="API access"
          description="Manage API keys for programmatic access"
          action={
            <Button variant="primary" size="sm" onClick={() => setShowCreateApiKeyModal(true)}>
              <Plus className="h-4 w-4" />
              Create API key
            </Button>
          }
        />
        {newApiKey && (
          <Alert variant="warning" className="mb-4" dismissible onDismiss={() => setNewApiKey(null)}>
            <strong>Save this key!</strong> It won't be shown again:
            <code className="block mt-2 p-2 bg-surface-secondary rounded text-body-xs break-all">
              {newApiKey}
            </code>
          </Alert>
        )}
        {apiKeysLoading ? (
          <LoadingState />
        ) : apiKeys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-text-muted mb-3">
              <KeyRound className="h-8 w-8" aria-hidden />
            </div>
            <p className="text-body-sm font-medium text-text-secondary">No API keys</p>
            <p className="text-body-xs text-text-tertiary mt-1 max-w-sm">
              Create an API key to access the SEQUESTO OS API programmatically
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between py-3 border-b border-border-light last:border-0"
              >
                <div>
                  <p className="text-body-sm font-medium text-text-primary">{key.name}</p>
                  <p className="text-body-xs text-text-tertiary">
                    {key.keyPrefix}… · Created {formatDayMonthYear(key.createdAt)}
                    {key.lastUsedAt && ` · Last used ${formatDayMonthYear(key.lastUsedAt)}`}
                  </p>
                  {isStaleKey(key) && (
                    <p className="mt-0.5 flex items-center gap-1.5 text-body-xs text-warning-dark">
                      <span className="h-1.5 w-1.5 flex-none rounded-full bg-warning" aria-hidden />
                      {key.lastUsedAt ? 'Unused for more than 60 days' : 'Never used, created over 60 days ago'}. Revoke it if
                      nothing needs it.
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm('Revoke this API key?')) {
                      deleteApiKey.mutate(key.id);
                    }
                  }}
                >
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      {/* Notifications Section */}
      <Card>
        <CardHeader
          title="Notifications"
          description="Configure notification preferences"
        />
        <div className="space-y-4">
          <NotificationToggle
            title="Email notifications"
            description="Receive email alerts for important events"
            enabled={notificationPrefs?.emailNotifications ?? true}
            onChange={(enabled) => updateNotificationPrefs.mutate({ emailNotifications: enabled })}
          />
          <NotificationToggle
            title="Sync failure alerts"
            description="Get notified when a sync job fails"
            enabled={notificationPrefs?.syncFailureAlerts ?? true}
            onChange={(enabled) => updateNotificationPrefs.mutate({ syncFailureAlerts: enabled })}
          />
          <NotificationToggle
            title="Workflow alerts"
            description="Get notified about workflow execution results"
            enabled={notificationPrefs?.workflowAlerts ?? true}
            onChange={(enabled) => updateNotificationPrefs.mutate({ workflowAlerts: enabled })}
          />
          <NotificationToggle
            title="Security alerts"
            description="Important security-related notifications"
            enabled={notificationPrefs?.securityAlerts ?? true}
            onChange={(enabled) => updateNotificationPrefs.mutate({ securityAlerts: enabled })}
          />
          {/* No job reads this preference while the weekly digest is paused, so
              a live switch would promise something that never happens. */}
          <NotificationToggle
            title="Weekly digest"
            description="Not active: the weekly digest is paused"
            enabled={false}
            disabled
            onChange={() => {}}
          />
        </div>
        {canAccess('organizer') && (
          <div id="organizer-reminders" className="mt-6 border-t border-border-light pt-5">
            <h3 className="text-body-sm font-medium text-text-primary">Organizer reminders</h3>
            <p className="mb-4 mt-0.5 text-body-xs text-text-tertiary">
              These run automatically and only reach your own inbox and Slack DMs. Per-reminder switches are not
              built yet; every send is logged.
            </p>
            <ReminderSettingsCard />
          </div>
        )}
      </Card>

      {/* Optional Sections (admin-only): hidden-by-default parts of the OS */}
      {user?.role === 'admin' && OPTIONAL_GROUPS.length > 0 && (
        <Card>
          <CardHeader
            title="Optional sections"
            description="Parts of the OS hidden by default. Switch one on to see it in your navigation."
          />
          <div className="space-y-5">
            {OPTIONAL_GROUPS.map((group) => (
              <div key={group.area}>
                <p className="eyebrow mb-1">{group.area}</p>
                <div className="grid gap-x-8 sm:grid-cols-2">
                  {group.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 border-b border-border-light py-2"
                    >
                      <span className="text-body-sm text-text-primary">{item.label}</span>
                      <Switch
                        label={`${group.area}: ${item.label}`}
                        enabled={(user?.access ?? []).includes(item.id)}
                        onChange={(enabled) =>
                          updateOptionalFeature.mutate(
                            { feature: item.id, enabled },
                            { onSuccess: () => refreshUser() },
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        currentName={user?.name || ''}
        currentEmail={user?.email || ''}
        onSave={(data) => {
          updateProfile.mutate(data, {
            onSuccess: () => setShowEditProfileModal(false),
          });
        }}
        isLoading={updateProfile.isPending}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSave={(data) => {
          changePassword.mutate(data, {
            onSuccess: () => {
              setShowChangePasswordModal(false);
              alert('Password changed. Please log in again.');
              window.location.href = '/login';
            },
          });
        }}
        isLoading={changePassword.isPending}
        error={changePassword.error?.message}
      />

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        isOpen={showCreateApiKeyModal}
        onClose={() => setShowCreateApiKeyModal(false)}
        onCreate={(data) => {
          createApiKey.mutate(data, {
            onSuccess: (response) => {
              setShowCreateApiKeyModal(false);
              setNewApiKey(response.apiKey.key || null);
            },
          });
        }}
        isLoading={createApiKey.isPending}
      />
    </PageShell>
  );
}

function ProfileField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-body-xs text-text-tertiary mb-1">{label}</p>
      <div className="text-body-sm text-text-primary">{value ?? <Nil />}</div>
    </div>
  );
}

function NotificationToggle({
  title,
  description,
  enabled,
  onChange,
  disabled = false,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border-light last:border-0">
      <div>
        <p className={`text-body-sm font-medium ${disabled ? 'text-text-tertiary' : 'text-text-primary'}`}>{title}</p>
        <p className="text-body-xs text-text-tertiary">{description}</p>
      </div>
      <Switch label={title} enabled={enabled} onChange={onChange} disabled={disabled} />
    </div>
  );
}

/** On = solid ink, off = light grey, so the state reads at a glance. */
function Switch({
  label,
  enabled,
  onChange,
  disabled = false,
}: {
  label: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-50 ${
        enabled ? 'bg-text-primary' : 'bg-border'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function EditProfileModal({
  isOpen,
  onClose,
  currentName,
  currentEmail,
  onSave,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  currentEmail: string;
  onSave: (data: { name?: string; email?: string }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updates: { name?: string; email?: string } = {};
    if (name !== currentName) updates.name = name;
    if (email !== currentEmail) updates.email = email;
    if (Object.keys(updates).length > 0) {
      onSave(updates);
    } else {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit profile">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ChangePasswordModal({
  isOpen,
  onClose,
  onSave,
  isLoading,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { currentPassword: string; newPassword: string }) => void;
  isLoading: boolean;
  error?: string;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (newPassword !== confirmPassword) {
      setValidationError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    onSave({ currentPassword, newPassword });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change password">
      <form onSubmit={handleSubmit} className="space-y-4">
        {(validationError || error) && (
          <Alert variant="error">{validationError || error}</Alert>
        )}
        <Input
          label="Current password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />
        <Input
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <Input
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function CreateApiKeyModal({
  isOpen,
  onClose,
  onCreate,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; expiresInDays?: number }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      name,
      expiresInDays: expiresInDays ? parseInt(expiresInDays) : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create API key">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Key name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My API Key"
          required
        />
        <div>
          <label className="text-body-sm font-medium text-text-primary block mb-2">
            Expiration (optional)
          </label>
          <select
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface-primary text-text-primary text-body-sm focus:outline-none focus:ring-2 focus:ring-border-dark"
          >
            <option value="">Never expires</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="180">6 months</option>
            <option value="365">1 year</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!name || isLoading}>
            {isLoading ? 'Creating…' : 'Create key'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
